import React, { useMemo, useState } from 'react';
import { Box, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Tooltip, Typography } from '@mui/material';
import { MONTH_TOTAL_ROW_ID } from '../utils/deptDisplay';

// Plain MUI table for the Daily Breakdown, styled like the Refund Overview table (solid blue
// header, white bold labels, centred cells). It consumes the same column definitions the page
// already builds in utils/departmentIncomeTrackerTableHeader.js (field/headerName/headerTooltip/
// width/align/valueGetter/toolTipGetter/renderCell/cellStyle), so the cell logic is unchanged -
// only the shell around it is simpler than the shared GlobalTable.
// Sorting applies to the day rows only; the month-total row (`footerRow`) always stays last.
// The Date column is sticky on horizontal scroll.
const HEADER_BG = '#1565C0';
// narrowest a column may get before the table scrolls sideways (fits "₹ 6,00,000" at this font size)
const MIN_COL_WIDTH = 84;
// Excel-style grid: a 1px line on every side of every cell.
const GRID_LINE = '1px solid #CBD5E1';
const HEADER_GRID_LINE = '1px solid rgba(255,255,255,0.35)';

// `maxHeight` (optional): the table scrolls inside this height with the header row and the month-total row pinned. Unset = the table
// is its natural height, exactly as before (the Accounts Manager page).
// `fit` (optional): the table always fits the width it is given and never scrolls sideways (tighter padding, headers may wrap, no
// minimum column width). With no `maxHeight` it grows to its full height and its header and month-total row stay in view as the PAGE scrolls
// (`overflow-x: clip` keeps the box from becoming a scroller of its own). Unset = as before.
// `dense` (optional): a smaller type for a narrow box.
export default function DailyBreakdownTable({ headers, rows, footerRow, loading, maxHeight, fit = false, dense = false }) {
  const [sort, setSort] = useState({ field: '', direction: 'asc' });

  const sortedRows = useMemo(() => {
    if (!sort.field) return rows;
    const dir = sort.direction === 'asc' ? 1 : -1;
    const key = sort.field === 'dayLabel' ? 'date' : sort.field;
    return [...rows].sort((a, b) => {
      const av = a[key] ?? 0;
      const bv = b[key] ?? 0;
      if (typeof av === 'string' || typeof bv === 'string') return String(av).localeCompare(String(bv)) * dir;
      return (Number(av) - Number(bv)) * dir;
    });
  }, [rows, sort]);

  const handleSort = (field) =>
    setSort((prev) => (prev.field === field && prev.direction === 'asc' ? { field, direction: 'desc' } : { field, direction: 'asc' }));

  const bodyRows = footerRow ? [...sortedRows, footerRow] : sortedRows;

  if (loading && !rows.length) return <Skeleton variant="rectangular" height={240} sx={{ borderRadius: '10px' }} />;

  return (
    <TableContainer sx={{ borderRadius: '4px', backgroundColor: '#fff', width: '100%', border: GRID_LINE, ...(maxHeight ? { maxHeight, overflow: 'auto' } : {}), ...(fit ? { overflowX: maxHeight ? 'hidden' : 'clip' } : {}) }}>
      {/* Fixed layout + the same width on every column = equal columns. The table fills its box
          (the page gives it 60% of the row), so spare width is shared out evenly; MIN_COL_WIDTH
          keeps a column from getting too narrow for its amount, falling back to a sideways scroll
          inside the box instead of squeezing the numbers. */}
      <Table size="small" stickyHeader={Boolean(maxHeight) || fit} sx={{ tableLayout: 'fixed', width: '100%', minWidth: fit ? 0 : headers.length * MIN_COL_WIDTH }}>
        <TableHead>
          <TableRow>
            {headers.map((col, idx) => (
              <TableCell
                key={col.field}
                align="center"
                sx={{
                  width: fit ? undefined : col.width, // fitting the box: equal shares of it, not each column's own pixel width
                  backgroundColor: HEADER_BG,
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: dense ? '0.68rem' : '0.78rem',
                  lineHeight: 1.2,
                  wordBreak: 'normal',
                  px: fit ? 0.5 : 1,
                  py: 1,
                  border: HEADER_GRID_LINE,
                  ...(idx === 0 && { position: 'sticky', left: 0, zIndex: 4 }),
                }}
              >
                <Tooltip title={col.headerTooltip || ''} arrow>
                  <span>
                    <TableSortLabel
                      active={sort.field === col.field}
                      hideSortIcon
                      direction={sort.field === col.field ? sort.direction : 'asc'}
                      onClick={() => handleSort(col.field)}
                      sx={{
                        color: '#fff !important',
                        position: 'relative',
                        // The arrow is taken out of the flow: MUI reserves its width beside the label, which
                        // pushed every header label off-centre. Now the label alone is centred in the cell and
                        // the arrow (shown only on the sorted column) hangs just to its right.
                        '& .MuiTableSortLabel-icon': {
                          color: '#fff !important',
                          position: 'absolute',
                          right: -18,
                          margin: 0,
                          fontSize: 16,
                        },
                      }}
                    >
                      {col.headerName}
                    </TableSortLabel>
                  </span>
                </Tooltip>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {bodyRows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={headers.length} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">No Data Found</Typography>
              </TableCell>
            </TableRow>
          ) : (
            bodyRows.map((row) => (
              <TableRow key={row.date} hover={row.date !== MONTH_TOTAL_ROW_ID}>
                {headers.map((col, idx) => {
                  const raw = row[col.field];
                  const value = col.valueGetter ? col.valueGetter({ value: raw, row }) : raw;
                  const content = col.renderCell ? col.renderCell({ row, value }) : value;
                  const tip = col.toolTipGetter ? col.toolTipGetter({ value: raw, row }) : '';
                  const style = col.cellStyle ? col.cellStyle({ row }) : {};
                  return (
                    <TableCell
                      key={col.field}
                      align="center"
                      title={tip || undefined}
                      sx={{
                        textAlign: 'center',
                        border: GRID_LINE,
                        px: fit ? 0.5 : 1,
                        py: 0.75,
                        fontSize: dense ? '0.7rem' : fit ? '0.76rem' : '0.8rem',
                        whiteSpace: 'nowrap',
                        ...(fit ? { overflowWrap: 'anywhere', whiteSpace: 'normal' } : { overflow: 'hidden', textOverflow: 'ellipsis' }),
                        backgroundColor: '#fff',
                        ...(idx === 0 && { position: 'sticky', left: 0, zIndex: 2 }),
                        ...style,
                        // scrolling inside a fixed height: the month-total row stays at the bottom of the box
                        ...((maxHeight || fit) && footerRow && row === footerRow && { position: 'sticky', bottom: 0, zIndex: idx === 0 ? 5 : 3 }),
                      }}
                    >
                      {content}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
